import { Fragment, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import "./booking.scss";
import MyInformationsContainer from "app/pages/Booking/MyInformations/MyInformationsContainer";
import { Navigate, Route, Routes, useLocation, useMatch } from "react-router-dom";
import { QUOTATION_STATUS_VALIDE, STRATEGY } from "app/constants";
import { connect } from "react-redux";
import OauthRoute from "app/pages/.shared/OauthRoute";
import BookingPrivateRoute from "app/pages/.shared/BookingPrivateRoute";
import { strategyShape } from "app/utils/propTypes";
import {
	AsyncAuthBookingContainer,
	AsyncBookingAsideContainer,
	AsyncBookingSearchContainer,
	AsyncConfirmationContainer,
	AsyncQuotationContainer,
	AsyncRestoreSessionContainer,
} from "app/routes";
import PaymentContainer from "./Payment/PaymentContainer";
import { isWhiteLabel } from "app/reducers/brandSelector";
import AppGlobalsContext from "app/AppGlobalsContext";
import UsefulInformation from "app/pages/FicheProduit/components/UsefulInformation/UsefulInformation";
import Collapse from "rc-collapse";
import { FormattedMessage } from "react-intl";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import CollapsableHolidaySummaryContainer from "app/pages/Booking/HolidaySummary/CollapsableHolidaySummary/CollapsableHolidaySummaryContainer";
import ContractRemarks from "app/pages/Booking/ContractRemarks";
import QueriesParser from "app/pages/.shared/QueriesParser/QueriesParser";
import SmartDPHotelDescriptionContainer from "app/pages/SmartDP/Quote/HotelDescription/SmartDPHotelDescriptionContainer";
import BookingLoadingPrice from "app/pages/Booking/BookingLoadingPrice";
import { isEmpty } from "lodash";
import { bindActionCreators } from "redux";
import { fetchProductStressMarketingsData } from "app/pages/StressMarketing/stressMarketingActionCreators";
import { isFlashsale } from "app/pages/FicheProduit/ficheProduitSelector";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import RequireSignupFirst from "app/pages/Auth/RequireSignupFirst";

const Booking = ({
	strategy,
	isWhiteLabel,
	ficheProduit,
	isFlashsale,
	quotationStatus,
	contractRemarks,
	dvaf = {},
	fetchProductStressMarketingsData = () => {},
}) => {
	const location = useLocation();
	const currentPathname = location.pathname;

	const { resolution } = useContext(AppGlobalsContext);

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const isSearchPage = useMatch("/booking/search");

	const isQuotationPage = useMatch("/booking/quote");

	const isRestoreBookingSessionPage = useMatch("/booking/restoreSession");

	const isPaymentPage = useMatch("/booking/payment");

	const isSDPPaymentPage = useMatch("/sdp/booking/payment");

	const isSDPQuotationPage = useMatch("/sdp/booking/quote");

	const isConfirmationPage = useMatch("/booking/confirmation");

	const isSDPConfirmationPage = useMatch("/sdp/booking/confirmation");

	const informationComponent = isSDPPaymentPage ? (
		<MyInformationsContainer />
	) : (
		<UsefulInformation usefulInformations={ficheProduit.usefulInformations} />
	);

	const isPropsEmpty = isSDPPaymentPage
		? isEmpty(dvaf) && isEmpty(dvaf.optional)
		: isEmpty(ficheProduit.usefulInformations);

	const accordionTitleId = isSDPPaymentPage
		? "payment.myinformations.fa.title"
		: "useful.information.title";

	const isSDP = currentPathname.includes("/sdp");

	// il faut verifier le quotationStatus pour eviter pb de sticky sur ios mobile
	const showHolidaySummary =
		!isSearchPage &&
		!isConfirmationPage &&
		!isSDPConfirmationPage &&
		isMobile &&
		QUOTATION_STATUS_VALIDE.includes(quotationStatus);

	const destinationAirportIATA = ficheProduit?.geography?.destinationAirportIATA;

	useEffect(() => {
		if (destinationAirportIATA) {
			fetchProductStressMarketingsData(destinationAirportIATA);
		}
	}, [destinationAirportIATA]);

	const shouldNotProtectedPaymentRoute =
		strategy === STRATEGY.TRANSACTION_FIRST ||
		((strategy === STRATEGY.AUTH_FLASHSALE ||
			strategy === STRATEGY.NAVIGATION_FIRST ||
			strategy === STRATEGY.OAUTH) &&
			!isFlashsale);

	const shouldProtecteQuotationRoute = strategy === STRATEGY.AUTH_FLASHSALE && isFlashsale;

	return (
		<Fragment>
			<div className="booking">
				<Routes>
					<Route
						path="/auth"
						element={
							isWhiteLabel ? (
								<AsyncAuthBookingContainer />
							) : (
								<Navigate
									to={{
										pathname: "/not-found",
									}}
								/>
							)
						}
					/>
				</Routes>
				{//display BookingLoadingPrice juste for booking/quote and not for /sdp/booking/quote
				!isMobile &&
					isQuotationPage &&
					!QUOTATION_STATUS_VALIDE.includes(quotationStatus) && <BookingLoadingPrice />}
				<div className="booking__container">
					<div className="booking__main">
						{isSDP && (
							<Routes>
								<Route
									path="/quote"
									element={<SmartDPHotelDescriptionContainer />}
								/>
							</Routes>
						)}

						<Routes>
							<Route path="/search" element={<AsyncBookingSearchContainer />} />
							<Route
								path="/quote"
								element={
									<QueriesParser>
										{shouldProtecteQuotationRoute ? (
											<RequireSignupFirst
												renderComponent={props => {
													return <AsyncQuotationContainer {...props} />;
												}}
											/>
										) : (
											<AsyncQuotationContainer />
										)}
									</QueriesParser>
								}
							/>
							<Route
								path="/payment"
								element={
									// eslint-disable-next-line no-nested-ternary
									strategy === STRATEGY.OAUTH ? (
										<OauthRoute>
											<PaymentContainer />
										</OauthRoute>
									) : shouldNotProtectedPaymentRoute ? (
										<PaymentContainer />
									) : (
										<BookingPrivateRoute authPath="../auth">
											<PaymentContainer />
										</BookingPrivateRoute>
									)
								}
							/>
							<Route
								path="/restoreSession"
								element={<AsyncRestoreSessionContainer />}
							/>
							<Route path="/confirmation" element={<AsyncConfirmationContainer />} />
						</Routes>
					</div>

					<div className="booking__aside">
						<Routes>
							<Route
								path="/search"
								element={
									<AsyncBookingAsideContainer showProductPicture showContactUs />
								}
							/>
							<Route
								path="/quote"
								element={
									<AsyncBookingAsideContainer
										showQuotationCTAButton
										showContactUs
										showPriceDetails
										showProductPicture={!isSDP}
									/>
								}
							/>
							<Route
								path="/payment"
								element={
									<AsyncBookingAsideContainer
										showProductPicture
										showContactUs
										showPriceDetails
									/>
								}
							/>
							<Route
								path="/restoreSession"
								element={
									<AsyncBookingAsideContainer
										showProductPicture
										showContactUs
										showPriceDetails
									/>
								}
							/>
							<Route
								path="/confirmation"
								element={<AsyncBookingAsideContainer showProductPicture />}
							/>
						</Routes>
					</div>
				</div>
				{showHolidaySummary && (
					<div className="booking__holiday-summary">
						<CollapsableHolidaySummaryContainer />
					</div>
				)}
			</div>
			<div className="booking-information__container">
				<div className="booking-information__content">
					{(isPaymentPage || isRestoreBookingSessionPage) &&
						!isEmpty(dvaf) &&
						!isEmpty(dvaf.optional) && (
							<Collapse
								expandIcon={({ isActive }) => {
									return (
										<i
											style={{
												transition: "transform .2s ease",
												transform: `rotate(${isActive ? 180 : 0}deg)`,
											}}
											className="icon icon--arrow-down-grey"
										/>
									);
								}}
							>
								<Collapse.Panel
									headerClass="booking-useful-information__header"
									header={
										<Typography variant={TYPOGRAPHY_VARIANTS.XL3} isBold>
											<FormattedMessage id="payment.myinformations.fa.title" />
										</Typography>
									}
								>
									<div className="booking-useful-information">
										{<MyInformationsContainer />}
									</div>
								</Collapse.Panel>
							</Collapse>
						)}

					{(isPaymentPage || isSDPPaymentPage || isRestoreBookingSessionPage) && (
						<ContractRemarks remarks={contractRemarks} />
					)}

					{(isSDPQuotationPage || isSDPPaymentPage) && !isPropsEmpty && (
						<Collapse
							expandIcon={({ isActive }) => {
								return (
									<i
										style={{
											transition: "transform .2s ease",
											transform: `rotate(${isActive ? 180 : 0}deg)`,
										}}
										className="icon icon--arrow-down-grey"
									/>
								);
							}}
						>
							<Collapse.Panel
								headerClass="booking-useful-information__header"
								header={
									<Typography variant={TYPOGRAPHY_VARIANTS.XL3} isBold>
										<FormattedMessage id={accordionTitleId} />
									</Typography>
								}
							>
								<div className="booking-useful-information">
									{informationComponent}
								</div>
							</Collapse.Panel>
						</Collapse>
					)}
				</div>
			</div>
		</Fragment>
	);
};

Booking.propTypes = {
	strategy: strategyShape,
	ficheProduit: PropTypes.object,
	isWhiteLabel: PropTypes.bool,
	quotationStatus: PropTypes.string,
	contractRemarks: PropTypes.string,
	isFlashsale: PropTypes.bool,
	dvaf: PropTypes.object,
	fetchProductStressMarketingsData: PropTypes.func,
};

const mapDispatchToProps = dispatch =>
	bindActionCreators(
		{
			fetchProductStressMarketingsData,
		},
		dispatch
	);

const mapStateToProps = state => {
	const offerType = state.booking.offer.type;
	return {
		dvaf: state.documents.dvaf[offerType] ? state.documents.dvaf[offerType] : undefined,
		ficheProduit: state.ficheProduit,
		isFlashsale: isFlashsale(state),
		strategy: state.partner.strategy,
		isWhiteLabel: isWhiteLabel(state),
		quotationStatus: state.quotation.status,
		contractRemarks: state.payment.contractRemarks,
	};
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(Booking);
